import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import Table from "react-bootstrap/Table";

// Firebase
import { db } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, query, getDocs, where } from "firebase/firestore";

import {
  DEFAULT_USD_TO_KES_RATE,
  fetchUsdToKesRate,
  formatMoney,
  getCurrencyConversionRate,
  resolvePaymentDisplay,
  resolveStudentCurrency,
} from "../utils/currency";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function FeeComp() {
  // States
  const [courseFeeInstallments, setCourseFeeInstallments] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [amountPaidThisMonth, setAmountPaidThisMonth] = useState("");
  const [monthlyData, setMonthlyData] = useState(Array(12).fill(0)); // Holds payments for each month
  const [studentsList, setStudentList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [currentCurrencyCode, setCurrentCurrencyCode] = useState("KES");
  const [usdToKesRate, setUsdToKesRate] = useState(DEFAULT_USD_TO_KES_RATE);
  const conversionRate = getCurrencyConversionRate(currentCurrencyCode, usdToKesRate);

  useEffect(() => {
    let isMounted = true;

    fetchUsdToKesRate(db).then((rate) => {
      if (isMounted) {
        setUsdToKesRate(rate);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          const uid = user.uid;
          setIsLoading(true);

          // Fetch student data and income data
          await fetchStudentData(uid);
          await fetchIncomeData(uid);

          try {
            // Query Firestore
            const q = query(collection(db, "Income"), where("studentId", "==", uid));
            const querySnapshot = await getDocs(q);

            // Transform and update state
            const studentsItem = querySnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            setStudentList(studentsItem);
          } catch (error) {
            console.error("Error fetching income data:", error);
          } finally {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      });
    };

    fetchData();
  }, []); // Empty dependency array means it runs only once
 

  // Fetch student course details
  const fetchStudentData = async (uid) => {
    const q = query(collection(db, "users"), where("userId", "==", uid));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      const course = userData.course;
      setCurrentCurrencyCode(resolveStudentCurrency(userData));
      fetchCoursePrice(course);
    });
  };

  // Fetch course price and installment details
  const fetchCoursePrice = async (course) => {
    const q = query(collection(db, "courses"), where("mcourseName", "==", course));
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      setCourseFeeInstallments(doc.data().feeInstallments);
    });
  };

  

  // Fetch income data
  const fetchIncomeData = async (uid) => {
    const q = query(collection(db, "Income"), where("studentId", "==", uid));
    const querySnapshot = await getDocs(q);

    let totalIncome = 0;
    let thisMonthIncome = 0;
    const tempMonthlyData = Array(12).fill(0);

    const today = new Date();
    const thisMonth = today.getMonth();
    const thisYear = today.getFullYear();

    querySnapshot.forEach((doc) => {
      const incomeAmount = parseInt(doc.data().incomeAmount);
      const paymentDate = doc.data().paymentDate.toDate();
      const paymentMonth = paymentDate.getMonth();
      const paymentYear = paymentDate.getFullYear();

      // Accumulate income
      totalIncome += incomeAmount;

      // Accumulate monthly data
      if (paymentYear === thisYear) {
        tempMonthlyData[paymentMonth] += incomeAmount;
        if (paymentMonth === thisMonth) {
          thisMonthIncome += incomeAmount;
        }
      }
    });

    setAmountPaid(totalIncome);
    setAmountPaidThisMonth(thisMonthIncome);
    setMonthlyData(tempMonthlyData);
  };

  // Prepare graph data
  const graphData = monthlyData.map((value) => (value * conversionRate).toFixed(2));

  return (
    <div>
      <div className="feeComp feeReportPage">
        <div className="feeReportHeader">
          <div>
            <h5>Fee Reports</h5>
            <p>Track your payments, monthly activity, and receipts in one place.</p>
          </div>
        </div>

        <div className="feeReportStats">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div className="feeReportStat feeReportStat--skeleton" key={`stat-skeleton-${index}`}>
                <div className="skeleton-line skeleton-line--title" />
                <div className="skeleton-line" />
              </div>
            ))
          ) : (
            <>
              <div className="feeReportStat">
                <span>Total Paid</span>
                <strong>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(amountPaid * conversionRate)} {currentCurrencyCode}
                </strong>
              </div>
              <div className="feeReportStat">
                <span>Paid This Month</span>
                <strong>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(amountPaidThisMonth * conversionRate)} {currentCurrencyCode}
                </strong>
              </div>
              <div className="feeReportStat">
                <span>Monthly Installment</span>
                <strong>
                  {new Intl.NumberFormat("en-US", {
                    style: "decimal",
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format((courseFeeInstallments || 0) * conversionRate)} {currentCurrencyCode}
                </strong>
              </div>
              <div className="feeReportStat">
                <span>Receipts</span>
                <strong>{studentsList.length}</strong>
              </div>
            </>
          )}
        </div>

        <div className="feeReportCard feeReportChart">
          <div className="feeReportCardHeader">
            <div>
              <h6>Monthly Payment Trend</h6>
              <p>Payments logged this year in {currentCurrencyCode}.</p>
            </div>
          </div>
          {isLoading ? (
            <div className="feeChartSkeleton">
              <div className="skeleton-line" />
              <div className="skeleton-line" />
              <div className="skeleton-line" />
            </div>
          ) : (
            <Line
              className="theFeeLineGraph"
              datasetIdKey="id"
              data={{
                labels: [
                  "Jan",
                  "Feb",
                  "March",
                  "Apr",
                  "May",
                  "June",
                  "July",
                  "Aug",
                  "Sept",
                  "Oct",
                  "Nov",
                  "Dec",
                ],
                datasets: [
                  {
                    id: 1,
                    label: `Fee payment - ${currentCurrencyCode}`,
                    data: graphData,
                    backgroundColor: "rgba(37, 99, 235, 0.12)",
                    borderColor: "#2563eb",
                    borderWidth: 2,
                    tension: 0.4,
                    pointRadius: 3,
                  },
                ],
              }}
            />
          )}
        </div>

        <div className="feeInvoiceHeader">
          <h6 className="feeTransactionHeading">Payment Transactions</h6>
          <p className="feeInvoiceSubtext">View each payment and download receipts when needed.</p>
        </div>

        <div className="feeTableCont table-responsive">
          <Table striped className="feeTable feeTablePretty">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Download Receipt</th>
              </tr>
            </thead>
            <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <tr key={`row-skeleton-${index}`}>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-line" /></td>
                  <td><div className="skeleton-button" /></td>
                </tr>
              ))
            ) : (
              studentsList.map((mystudent) => {
                const paymentDisplay = resolvePaymentDisplay(
                  mystudent,
                  { currency: currentCurrencyCode },
                  usdToKesRate
                );

                return (
                  <tr key={mystudent.id}>
                    <td>{mystudent.paymentDate.toDate().toDateString()}</td>
                    <td>{formatMoney(paymentDisplay.amount, paymentDisplay.currency)}</td>
                    <td>{mystudent.paymentMethod}</td>
                    <td> <a href={'/view-receipt?' + mystudent.invoiceNumber} target="_blank" className='btn btn-warning feeButton' rel="noreferrer">View Receipt</a> </td>
                  </tr>
                );
              })
            )}  
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
}

export default FeeComp;
